import axios from '@/api/axios-notifications'
import router from '@/router'
import qs from 'qs'
import { DateTime } from 'luxon'
import { downloadCsv, objectsToCsv } from '@/helpers/csv_exporter'
import notificationFeature from '@/constants/notification/notification-feature.js'
import { getDateWithThaiTimezoneFromDate } from '@/helpers/date_calculator'
import { formatQuantityInt } from '@/helpers/string_formatter'

import { newPreference as notiPreference } from '@/constants/notification/notification-feature.js'
import notificationTrigger from '@/constants/notification/notification-trigger.js'

const state = {
  notification: null,
  notifications: [],
  manualNotification: undefined,
  manualNotifications: [],
  autoNotification: undefined,
  autoNotifications: [],
  allNotifications: [],
  searchNotifications: [],
  notificationsTotalPage: 0,
  searchNotificationsTotalPage: 0,
  notificationIsLoading: false,
  notificationsIsLoading: false,
  allNotificationsIsLoading: false,
  searchNotificationsIsLoading: false,
  popupOrderOptions: [],
  appVersionOptions: [],
}

const mutations = {
  setNotification(state, notification) {
    state.notification = notification
  },
  setNotifications(state, notifications) {
    state.notifications = notifications
  },
  setManualNotifications(state, notifications) {
    state.manualNotifications = notifications
  },
  setManualNotification(state, notification) {
    state.manualNotification = notification
  },
  setAutoNotifications(state, notifications) {
    state.autoNotifications = notifications
  },
  setAutoNotification(state, notification) {
    state.autoNotification = notification
  },
  setSearchNotifications(state, notifications) {
    state.searchNotifications = notifications
  },
  setNotificationsTotalPage(state, pages) {
    state.notificationsTotalPage = pages
  },
  setSearchNotificationsTotalPage(state, pages) {
    state.searchNotificationsTotalPage = pages
  },
  setNotificationIsLoading(state, status) {
    state.notificationIsLoading = status
  },
  setNotificationsIsLoading(state, status) {
    state.notificationsIsLoading = status
  },
  setAllNotifications(state, allNotifications) {
    state.allNotifications = allNotifications
  },
  setAllNotificationsIsLoading(state, status) {
    state.allNotificationsIsLoading = status
  },
  setSearchNotificationsIsLoading(state, status) {
    state.searchNotificationsIsLoading = status
  },
  setPopupOrderOptions(state, status) {
    state.popupOrderOptions = status
  },
  setAppVersionOptions(state, status) {
    state.appVersionOptions = status
  },
}

const actions = {
  getManualNotifications: async (
    { commit, getters },
    {
      page = 1,
      limit = 10,
      query = null,
      preferences = null,
      // triggers = null,
      userType = null,
      start = null,
      end = null,
      order = null,
      status = null,
    }
  ) => {
    let params = {
      page: page,
      limit: limit,
      query: query,
      user_types: userType,
      preferences: preferences,
      start_date: start,
      end_date: end,
      order: order,
      status: status,
    }

    params = cleanParams(params)

    if ((start != null) & (end != null)) {
      params.start_date = getDateWithThaiTimezoneFromDate(
        start,
        'start'
      ).toISOString()
      params.end_date = getDateWithThaiTimezoneFromDate(
        end,
        'end'
      ).toISOString()
    }

    commit('setNotificationsIsLoading', true)
    await axios
      .get(`/manual-notification-settings`, {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })
      .then((res) => {
        if (res.data) {
          let data = res.data.notifications.map((e) => {
            return {
              ...e,
              start_date: e.schedule.start_date || '',
              end_date: e.schedule.end_date || '',
              start_time: e.schedule.start_time || '',
              end_time: e.schedule.end_time || '',
              status: capitalizeFirstLetter(e.status),
            }
          })
          commit('setNotificationsTotalPage', res.data.meta.pagination.pages)
          commit('setManualNotifications', data)
        } else {
          commit('setManualNotifications', undefined)
        }
        commit('setNotificationsIsLoading', false)
      })
      .catch((err) => {
        commit('setNotificationsIsLoading', false)
      })
  },

  getSearchManualNotifications: async (
    { commit, getters },
    { page = 1, limit = 10, query = null }
  ) => {
    commit('setSearchNotificationsIsLoading', true)
    let params = {
      page: page,
      limit: limit,
      query: query,
    }
    // Filter out properties with null values
    params = cleanParams(params)

    commit('setNotificationsIsLoading', true)
    await axios
      .get(`/manual-notification-settings`, {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })
      .then((res) => {
        if (res.data) {
          let data = res.data.notifications.map((e) => {
            return {
              ...e,
              start_date: e.schedule.start_date || '',
              end_date: e.schedule.end_date || '',
              start_time: e.schedule.start_time || '',
              end_time: e.schedule.end_time || '',
              status: capitalizeFirstLetter(e.status),
            }
          })
          commit('setNotificationsTotalPage', res.data.meta.pagination.pages)
          commit('setManualNotifications', data)
        } else {
          commit('setManualNotifications', undefined)
        }
        commit('setNotificationsIsLoading', false)
      })
      .catch((err) => {
        commit('setNotificationsIsLoading', false)
      })
  },
  getSearchManualNotificationReport: async (
    { commit, getters },
    {
      query = null,
      preferences = null,
      userType = null,
      start_date = null,
      end_date = null,
    }
  ) => {
    let params = {
      page: 1,
      limit: 9999,
      query: query,
      user_types: userType,
      preferences: preferences,
      start_date: start_date,
      end_date: end_date,
    }

    if ((start_date != null) & (end_date != null)) {
      params.start_date = getDateWithThaiTimezoneFromDate(
        start_date,
        'start'
      ).toISOString()
      params.end_date = getDateWithThaiTimezoneFromDate(
        end_date,
        'end'
      ).toISOString()
    }

    params = cleanParams(params)

    try {
      const res = await axios.get(`/manual-notification-settings`, {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })
      if (res.data?.notifications.length > 0) {
        let data = res.data.notifications.map((e) => ({
          id: e.id || '',
          image_url: e.image_url || '',
          is_start_immediately: e.is_start_immediately || '',
          order: e.order || '',
          preference: e.preference || '',
          start_date: e.schedule.start_date || '',
          end_date: e.schedule.end_date || '',
          start_time: e.schedule.start_time || '',
          end_time: e.schedule.end_time || '',
          status: e.status || '',
          title: e.title || '',
          trigger: e.trigger || '',
          type: e.type || '',
          all_app_versions: e.audience_setting.all_app_versions || '',
          all_countries: e.audience_setting.all_countries || '',
          all_provinces: e.audience_setting.all_provinces || '',
          app_version: e.audience_setting.app_version || '',
          countries: e.audience_setting.countries?.join(', ') || '',
          start_age: e.audience_setting.start_age || '',
          end_age: e.audience_setting.end_age || '',
          genders: e.audience_setting.genders?.join(', ') || '',
          languages: e.audience_setting.languages?.join(', ') || '',
          last_actives: e.audience_setting.last_actives || '',
          operating_systems:
            e.audience_setting.operating_systems?.join(', ') || '',
          province_ids: e.audience_setting.province_ids || '',
          subscription_plans:
            e.audience_setting.subscription_plans?.join(', ') || '',
          targeting_strategy: e.audience_setting.targeting_strategy || '',
          user_group_name: e.audience_setting.user_group_name || '',
          user_ids: e.audience_setting.user_ids?.join(', ') || '',
          user_types: e.audience_setting.user_types?.join(', ') || '',
          deep_link: e.deep_link || '',
          sent_rate: e.report.sent_rate || '',
          open_rate: e.report.open_rate || '',
        }))
        let csvFormat = objectsToCsv(data)
        downloadCsv(csvFormat, `notification_${new Date().getTime()}.csv`)

        return { success: true }
      } else {
        return { success: false, message: 'Export Fail: no data' }
      }
    } catch (err) {
      return { success: false, message: err.message }
    }
  },

  getManualNotificationById: async ({ commit, getters }, id) => {
    commit('setNotification', null)
    commit('setNotificationsIsLoading', true)
    await axios
      .get(`/manual-notification-settings/${id}`)
      .then((res) => {
        if (res.data) {
          let report = getReportData(res.data.report)
          let schedule = res.data.schedule
          res.data.schedule = {
            ...schedule,
            start_date: getDate(schedule.start_date),
            end_date: getDate(schedule.end_date),
            start_time: getTimeHMFromTimestamptz(schedule.start_date),
            end_time: getTimeHMFromTimestamptz(schedule.end_date),
          }
          res.data.metrices = report
          commit('setManualNotification', res.data)
        } else {
          commit('setNotification', null)
        }
        commit('setNotificationsIsLoading', false)
      })
      .catch((err) => {
        commit('setNotificationsIsLoading', false)
      })
  },
  createNotification: async ({ commit, dispatch }, noti) => {
    commit('setNotificationsIsLoading', true)
    await axios
      .post(`/manual-notification-settings`, noti)
      .then((res) => {
        commit('setNotificationsIsLoading', false)
        dispatch('setAlertMessage', {
          message: 'Create Notification Successfully!',
          type: 'success',
        })
        commit('setNotification', null)
        router.go(-1)
      })
      .catch((err) => {
        // if (err.response.status == 422) {
        //   let field = err.response.data.field_errors
        //   let errMsg = Object.entries(field)
        //     .map(([key, value]) => `${key} ${value}`)
        //     .join('\n')
        //   dispatch('setAlertMessage', {
        //     message: 'Error: ' + errMsg,
        //     type: 'error',
        //   })
        //   return
        // }
        let errrorMessage = err.response.data.error
        dispatch('setAlertMessage', {
          message: 'Error: ' + errrorMessage,
          type: 'error',
        })

        commit('setNotificationsIsLoading', false)
      })
  },
  editNotification: async ({ commit, dispatch }, { id, form }) => {
    commit('setNotificationsIsLoading', true)
    await axios
      .put(`/manual-notification-settings/${id}`, form)
      .then((res) => {
        commit('setNotificationsIsLoading', false)
        dispatch('setAlertMessage', {
          message: 'Notification Update Successfully!',
          type: 'success',
        })
        commit('setNotification', null)
        router.go(-1)
      })
      .catch((err) => {
        let errrorMessage = err.response.data.error
        dispatch('setAlertMessage', {
          message: 'Error: ' + errrorMessage,
          type: 'error',
        })
        commit('setNotificationsIsLoading', false)
      })
  },
  deleteNotification: async ({ commit, dispatch }, id) => {
    commit('setNotificationsIsLoading', true)
    await axios
      .delete(`/manual-notification-settings/${id}`)
      .then((res) => {
        commit('setNotificationsIsLoading', false)
        // dispatch('setAlertMessage', {
        //   message: 'Notification Update Successfully!',
        //   type: 'success',
        // })
        commit('setNotification', null)
        // router.go(-1)
      })
      .catch((err) => {
        let errrorMessage = err.response.data.error
        dispatch('setAlertMessage', {
          message: 'Error: ' + errrorMessage,
          type: 'error',
        })
        commit('setNotificationsIsLoading', false)
      })
  },
  getPopupOrderOptions: async ({ commit, dispatch }) => {
    commit('setNotificationsIsLoading', true)
    await axios
      .get(`/manual-notification-settings/popup-order`)
      .then((res) => {
        commit('setPopupOrderOptions', res.data)
      })
      .catch((err) => {})
  },
  getAppVersionOptions: async ({ commit, dispatch }) => {
    commit('setNotificationsIsLoading', true)
    await axios
      .get(`/manual-notification-settings/app-version`)
      .then((res) => {
        commit('setAppVersionOptions', res.data)
      })
      .catch((err) => {})
  },
  getAutoNotifications: async (
    { commit, getters },
    { page = 1, limit = 10, preferences = null, userType = null }
  ) => {
    let params = {
      page: page,
      limit: limit,
      preferences: preferences,
      user_types: userType,
    }

    params = cleanParams(params)

    commit('setNotificationIsLoading', true)
    await axios
      .get(`/auto-notification-settings`, {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })
      .then((res) => {
        if (res.data) {
          let notis = res.data.data
          notis = notis.map((e) => {
            const PRIORITY = ['all', 'premium', 'trial', 'basic']
            const key = PRIORITY.find((k) => k in e.contents_by_user_type)
            let title = ''
            if (key) {
              title =
                e.contents_by_user_type[key]['contents'][0].data_by_language[
                  'th'
                ].title
            }
            const userTypes = Object.keys(e.contents_by_user_type)
            let userTypeDisplay = 'all'
            if (Array.isArray(userTypes)) {
              if (userTypes.includes('all')) {
                userTypeDisplay = 'All'
              } else if (userTypes.length == 1) {
                if (userTypes.includes('basic')) {
                  userTypeDisplay = 'Basic'
                }
                if (userTypes.includes('trial')) {
                  userTypeDisplay = 'Trial'
                }
                if (userTypes.includes('premium')) {
                  userTypeDisplay = 'Premium'
                }
              } else {
                if (
                  userTypes.includes('basic') &&
                  userTypes.includes('trial')
                ) {
                  userTypeDisplay = 'Ba & Tri'
                }
                if (
                  userTypes.includes('trial') &&
                  userTypes.includes('premium')
                ) {
                  userTypeDisplay = 'Pre & Tri'
                }
                if (
                  userTypes.includes('premium') &&
                  userTypes.includes('basic')
                ) {
                  userTypeDisplay = 'Pre & Ba'
                }
              }
            }
            return {
              ...e,
              title: title,
              user_type: userTypeDisplay,
            }
          })
          commit('setNotificationsTotalPage', res.data.meta.pagination.pages)
          commit('setAutoNotifications', notis)
        } else {
          commit('setAutoNotifications', undefined)
        }
        commit('setNotificationIsLoading', false)
      })
      .catch((err) => {
        commit('setNotificationIsLoading', false)
      })
  },
  getAutoNotificationsReport: async (
    { commit, getters },
    { preferences = null, userType = null }
  ) => {
    let params = {
      page: 1,
      limit: 9999,
      preferences: preferences,
      user_types: userType,
    }

    params = cleanParams(params)

    try {
      const res = await axios.get(`/auto-notification-settings`, {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })

      if (res.data) {
        let raw = res.data.data
        raw = raw.map((e) => {
          const PRIORITY = ['all', 'premium', 'trial', 'basic']
          const key = PRIORITY.find((k) => k in e.contents_by_user_type)
          const title =
            e.contents_by_user_type[key]['contents'][0].data_by_language['th']
              .title
          const userTypes = Object.keys(e.contents_by_user_type)
          let userTypeDisplay = 'all'
          if (Array.isArray(userTypes)) {
            if (userTypes.includes('all')) {
              userTypeDisplay = 'All'
            } else if (userTypes.length == 1) {
              if (userTypes.includes('basic')) {
                userTypeDisplay = 'Basic'
              }
              if (userTypes.includes('trial')) {
                userTypeDisplay = 'Trial'
              }
              if (userTypes.includes('premium')) {
                userTypeDisplay = 'Premium'
              }
            } else {
              if (userTypes.includes('basic') && userTypes.includes('trial')) {
                userTypeDisplay = 'Ba & Tri'
              }
              if (
                userTypes.includes('trial') &&
                userTypes.includes('premium')
              ) {
                userTypeDisplay = 'Pre & Tri'
              }
              if (
                userTypes.includes('premium') &&
                userTypes.includes('basic')
              ) {
                userTypeDisplay = 'Pre & Ba'
              }
            }
          }
          return {
            ...e,
            title: title,
            user_type: userTypeDisplay,
          }
        })
        const csvData = raw.map((e) => {
          let { contents_by_user_type, ...data } = e
          const userTypes = Object.keys(e.contents_by_user_type)
          let deeplinks = []
          userTypes.forEach((userType) => {
            const userDeeplinkString = `${userType}: ${e.contents_by_user_type[userType].deep_link || ''}`
            deeplinks.push(userDeeplinkString)
          })
          function formatId(id, length = 2) {
            return 'N' + String(id).padStart(length, '0')
          }
          return {
            id: formatId(data.id),
            title: data.title,
            trigger: notificationTrigger[data.trigger],
            preference: notificationFeature[data.preference],
            time: data.time,
            deeplinks: deeplinks.join('\n'),
            user_type: data.user_type,
            is_active: data.is_active,
          }
        })

        let csvFormat = objectsToCsv(csvData)
        downloadCsv(csvFormat, `auto_notification_${new Date().getTime()}.csv`)
        return { success: true }
      } else {
        return { success: false, message: 'Export failed: no data' }
      }
    } catch (err) {
      return { success: false, message: err.message }
    }
  },

  getAutoNotification: async ({ commit, getters }, id) => {
    await axios
      .get(`/auto-notification-settings/${id}`)
      .then((res) => {
        if (res.data) {
          let noti = res.data
          const userTypes = Object.keys(noti.contents_by_user_type)
          let textSets = []
          let contentByUserType = userTypes.map((e) => {
            textSets = noti.contents_by_user_type[e]['contents'].map((e, i) => {
              return {
                id: i + 1,
                th: e.data_by_language['th'],
                en: e.data_by_language['en'],
              }
            })
            return {
              user_type: capitalizeFirstLetter(e),
              deeplink: noti.contents_by_user_type[e]['deep_link'],
            }
          })
          noti.contents = contentByUserType
          noti.text_sets = textSets
          commit('setAutoNotification', noti)
        } else {
          commit('setAutoNotification', undefined)
        }
        commit('setNotificationIsLoading', false)
      })
      .catch((err) => {
        commit('setNotificationIsLoading', false)
      })
  },
  getNotification: async ({ commit, getters }, id) => {
    commit('setNotificationIsLoading', true)

    await axios
      .get(`/notification-trigger/history/${id}`)
      .then((res) => {
        if (res.data) {
          commit('setNotification', res.data)
        } else {
          commit('setNotification', undefined)
        }
        commit('setNotificationIsLoading', false)
      })
      .catch((err) => {
        commit('setNotificationIsLoading', false)
      })
  },
  getNotifications: async (
    { commit, getters },
    {
      page = 1,
      limit = 15,
      query = null,
      features = null,
      triggers = null,
      userType = null,
      start = null,
      end = null,
    }
  ) => {
    commit('setNotificationsIsLoading', true)
    let params = {
      page: page,
      limit: limit,
      query: query,
      userType: userType,
      features: features,
      triggers: triggers,
      start: start,
      end: end,
    }

    // Filter out properties with null values
    params = cleanParams(params)

    await axios
      .get('/notification-trigger/history', {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })
      .then((res) => {
        if (res.data.notifications !== null) {
          commit('setNotifications', res.data.notifications)
          commit('setNotificationsTotalPage', res.data.meta.pagination.pages)
        } else {
          commit('setNotifications', [])
          // commit('setNotificationsTotalPage', 0)
        }
        commit('setNotificationsIsLoading', false)
      })
      .catch((err) => {
        commit('setNotificationsIsLoading', false)
      })
  },
  getSearchNotifications: async (
    { commit, getters },
    { page = 1, limit = 10, query = null }
  ) => {
    commit('setSearchNotificationsIsLoading', true)
    let params = {
      page: page,
      limit: limit,
      query: query,
    }

    // Filter out properties with null values
    params = cleanParams(params)

    await axios
      .get('/notification-trigger/history', {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })
      .then((res) => {
        if (res.data.notifications !== null) {
          commit('setSearchNotifications', res.data.notifications)
          commit(
            'setSearchNotificationsTotalPage',
            res.data.meta.pagination.pages
          )
        } else {
          commit('setSearchNotifications', [])
        }
        commit('setSearchNotificationsIsLoading', false)
      })
      .catch((err) => {
        commit('setSearchNotificationsIsLoading', false)
      })
  },
  getNotificationHistoryReport: async (
    { commit, getters },
    { query = null }
  ) => {
    let params = {
      page: 1,
      limit: 9999,
      query: query,
    }

    params = cleanParams(params)

    try {
      const res = await axios.get('/notification-trigger/history', {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })

      function getTotalUsers(noti) {
        return (
          (noti.undefined_count ?? 0) +
          (noti.ios_count ?? 0) +
          (noti.android_count ?? 0)
        )
      }

      function getNotificationSentRate(noti) {
        const totalUsers = getTotalUsers(noti)
        return (
          (getNotificationSent(noti) * 100) / (totalUsers > 0 ? totalUsers : 1)
        )
      }

      function getNotificationSent(noti) {
        return (
          (noti.undefined_sent_count ?? 0) +
          (noti.ios_sent_count ?? 0) +
          (noti.android_sent_count ?? 0)
        )
      }
      function getNotificationOpen(noti) {
        return (
          (noti.undefined_open_count ?? 0) +
          (noti.ios_open_count ?? 0) +
          (noti.android_open_count ?? 0)
        )
      }

      function getNotificationOpenRate(noti) {
        const totalUsers = getTotalUsers(noti)
        return (
          (getNotificationOpen(noti) * 100) / (totalUsers > 0 ? totalUsers : 1)
        )
      }

      function getNotificationFeature(feature) {
        return notiPreference[feature] || feature
      }

      function getNotificationTrigger(trigger) {
        return notificationTrigger[trigger] || snakeToPascalCase(trigger)
      }

      if (res.data.notifications !== null) {
        let data = res.data.notifications.map((e) => ({
          id: e.id,
          date: e.created_at,
          title: e.title_th || e.title_en || '',
          message: e.body_th || e.title_en || '',
          'noti type': getNotificationFeature(e.feature),
          trigger: getNotificationTrigger(e.trigger),
          'user type': e.user_type,
          'deep link': e.deep_link,
          'total users': getTotalUsers(e),
          sent: getNotificationSent(e),
          'sent rate': formatQuantityInt(getNotificationSentRate(e)) + '%',
          open: getNotificationOpen(e),
          'open rate': formatQuantityInt(getNotificationOpenRate(e)) + '%',
        }))
        let csvFormat = objectsToCsv(data)
        downloadCsv(csvFormat, `notification_${new Date().getTime()}.csv`)
        return { success: true }
      } else {
        return { false: true, message: 'Export failed: no data' }
      }
    } catch (err) {
      return { success: false, message: err.message }
    }
  },
  getAllNotifications: async (
    { commit, getters },
    {
      query = null,
      preferences = null,
      triggers = null,
      userType = null,
      start = null,
      end = null,
    }
  ) => {
    commit('setAllNotificationsIsLoading', true)
    let params = {
      query: query,
      userType: userType,
      preferences: preferences,
      triggers: triggers,
      start: start,
      end: end,
    }
    // Filter out properties with null values
    params = cleanParams(params)

    await axios
      .get('/notification-trigger/history-all', {
        params: params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        },
      })
      .then((res) => {
        if (res.data.notifications !== null) {
          commit('setAllNotifications', res.data.notifications)
        } else {
          commit('setAllNotifications', [])
        }
        commit('setAllNotificationsIsLoading', false)
      })
      .catch((err) => {
        commit('setAllNotificationsIsLoading', false)
      })
  },
  getAutoNotificationSendReport: async (
    { commit, getters, dispatch },
    { start_date = null, end_date = null }
  ) => {
    let params = {
      start_date: start_date,
      end_date: end_date,
    }

    params = cleanParams(params)

    commit('setIsExportingUser', true)
    await axios
      .get('/auto-notification/reports/sends', {
        params: params,
        responseType: 'blob',
      })
      .then((res) => {
        commit('setIsExportingUser', false)
        const BOM = '\uFEFF'
        const blob = new Blob([BOM, res.data], {
          type: 'text/csv;charset=utf-8',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'report.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        dispatch('setAlertMessage', {
          message: 'ดาวน์โหลดเสร็จสิ้น',
          type: 'success',
        })
      })
      .catch((err) => {
        commit('setIsExportingUser', false)
        if (err.response.status == 404) {
          dispatch('setAlertMessage', {
            message: 'Result not found',
            type: 'warning',
          })
        } else {
          dispatch('setAlertMessage', {
            message: 'Server Error',
            type: 'red darken-3',
          })
        }
      })
  },
  getAutoNotificationBreakdownReport: async (
    { commit, getters, dispatch },
    { start_date = null, end_date = null, tracking_id = null }
  ) => {
    let params = {
      start_date: start_date,
      end_date: end_date,
      tracking_id: tracking_id,
    }

    params = cleanParams(params)

    commit('setIsExportingUser', true)
    await axios
      .get('/auto-notification/reports/breakdown', {
        params: params,
        responseType: 'blob',
      })
      .then((res) => {
        commit('setIsExportingUser', false)
        const BOM = '\uFEFF'
        const blob = new Blob([BOM, res.data], {
          type: 'text/csv;charset=utf-8',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'report.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        dispatch('setAlertMessage', {
          message: 'ดาวน์โหลดเสร็จสิ้น',
          type: 'success',
        })
      })
      .catch((err) => {
        commit('setIsExportingUser', false)
        if (err.response.status == 404) {
          dispatch('setAlertMessage', {
            message: 'Result not found',
            type: 'warning',
          })
        } else {
          dispatch('setAlertMessage', {
            message: 'Server Error',
            type: 'red darken-3',
          })
        }
      })
  },
  getAutoNotificationRecipientReport: async (
    { commit, getters, dispatch },
    { tracking_id = null, setting_id = null }
  ) => {
    let params = {
      tracking_id: tracking_id,
      setting_id: setting_id,
    }

    params = cleanParams(params)

    commit('setIsExportingUser', true)
    await axios
      .get('/auto-notification/reports/recipients', {
        params: params,
        responseType: 'blob',
      })
      .then((res) => {
        commit('setIsExportingUser', false)
        const BOM = '\uFEFF'
        const blob = new Blob([BOM, res.data], {
          type: 'text/csv;charset=utf-8',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'report.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        dispatch('setAlertMessage', {
          message: 'ดาวน์โหลดเสร็จสิ้น',
          type: 'success',
        })
      })
      .catch((err) => {
        commit('setIsExportingUser', false)
        if (err.response.status == 404) {
          dispatch('setAlertMessage', {
            message: 'Result not found',
            type: 'warning',
          })
        } else {
          dispatch('setAlertMessage', {
            message: 'Server Error',
            type: 'red darken-3',
          })
        }
      })
  },
  getManualNotificationSendReport: async (
    { commit, getters, dispatch },
    { start_date = null, end_date = null }
  ) => {
    let params = {
      start_date: start_date,
      end_date: end_date,
    }

    params = cleanParams(params)

    commit('setIsExportingUser', true)
    await axios
      .get('/manual-notification/reports/sends', {
        params: params,
        responseType: 'blob',
      })
      .then((res) => {
        commit('setIsExportingUser', false)
        const BOM = '\uFEFF'
        const blob = new Blob([BOM, res.data], {
          type: 'text/csv;charset=utf-8',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'report.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        dispatch('setAlertMessage', {
          message: 'ดาวน์โหลดเสร็จสิ้น',
          type: 'success',
        })
      })
      .catch((err) => {
        commit('setIsExportingUser', false)
        if (err.response.status == 404) {
          dispatch('setAlertMessage', {
            message: 'Result not found',
            type: 'warning',
          })
        } else {
          dispatch('setAlertMessage', {
            message: 'Server Error',
            type: 'red darken-3',
          })
        }
      })
  },
  getManualNotificationBreakdownReport: async (
    { commit, getters, dispatch },
    { start_date = null, end_date = null, setting_id = null }
  ) => {
    let params = {
      start_date: start_date,
      end_date: end_date,
      setting_id: setting_id,
    }

    params = cleanParams(params)

    commit('setIsExportingUser', true)
    await axios
      .get('/manual-notification/reports/breakdown', {
        params: params,
        responseType: 'blob',
      })
      .then((res) => {
        commit('setIsExportingUser', false)
        const BOM = '\uFEFF'
        const blob = new Blob([BOM, res.data], {
          type: 'text/csv;charset=utf-8',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'report.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        dispatch('setAlertMessage', {
          message: 'ดาวน์โหลดเสร็จสิ้น',
          type: 'success',
        })
      })
      .catch((err) => {
        commit('setIsExportingUser', false)
        if (err.response.status == 404) {
          dispatch('setAlertMessage', {
            message: 'Result not found',
            type: 'warning',
          })
        } else {
          dispatch('setAlertMessage', {
            message: 'Server Error',
            type: 'red darken-3',
          })
        }
      })
  },
  getManualNotificationRecipientReport: async (
    { commit, getters, dispatch },
    { setting_id = null }
  ) => {
    let params = {
      setting_id: setting_id,
    }

    params = cleanParams(params)

    commit('setIsExportingUser', true)
    await axios
      .get('/manual-notification/reports/recipients', {
        params: params,
        responseType: 'blob',
      })
      .then((res) => {
        commit('setIsExportingUser', false)
        const BOM = '\uFEFF'
        const blob = new Blob([BOM, res.data], {
          type: 'text/csv;charset=utf-8',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'report.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        dispatch('setAlertMessage', {
          message: 'ดาวน์โหลดเสร็จสิ้น',
          type: 'success',
        })
      })
      .catch((err) => {
        commit('setIsExportingUser', false)
        if (err.response.status == 404) {
          dispatch('setAlertMessage', {
            message: 'Result not found',
            type: 'warning',
          })
        } else {
          dispatch('setAlertMessage', {
            message: 'Server Error',
            type: 'red darken-3',
          })
        }
      })
  },
}

function getReportData(report) {
  const getOsCountInfo = (os) => {
    const list = report?.devices_by_os
    if (!Array.isArray(list) || list.length === 0) return 0

    const item = list.find((d) => d?.os === os)
    return item?.count ?? 0
  }

  const getOsOpenInfo = (os) => {
    const list = report?.opened_by_os
    if (!Array.isArray(list) || list.length === 0) return 0

    const item = list.find((d) => d?.os === os)
    return item?.count ?? 0
  }

  const getOsSendInfo = (os) => {
    const list = report?.sent_by_os
    if (!Array.isArray(list) || list.length === 0) return 0

    const item = list.find((d) => d?.os === os)
    return item?.count ?? 0
  }

  let reportDetail = {
    undefined_count: 0,
    undefined_sent_count: 0,
    undefined_open_count: 0,
    ios_count: getOsCountInfo('ios'),
    ios_sent_count: getOsSendInfo('ios'),
    ios_open_count: getOsOpenInfo('ios'),
    android_count: getOsCountInfo('android'),
    android_sent_count: getOsSendInfo('android'),
    android_open_count: getOsOpenInfo('android'),
    ...report,
  }

  return reportDetail
}

const getters = {
  notification(state) {
    return state.notification
  },
  notifications(state) {
    return state.notifications
  },
  manualNotification(state) {
    return state.manualNotification
  },
  manualNotifications(state) {
    return state.manualNotifications
  },
  autoNotification(state) {
    return state.autoNotification
  },
  autoNotifications(state) {
    return state.autoNotifications
  },
  notificationsTotalPage(state) {
    return state.notificationsTotalPage
  },
  notificationIsLoading(state) {
    return state.notificationIsLoading
  },
  notificationsIsLoading(state) {
    return state.notificationsIsLoading
  },
  allNotifications(state) {
    return state.allNotifications
  },
  allNotificationsIsLoading(state) {
    return state.allNotificationsIsLoading
  },
  searchNotifications(state) {
    return state.searchNotifications
  },
  searchNotificationsIsLoading(state) {
    return state.searchNotificationsIsLoading
  },
  searchNotificationsTotalPage(state) {
    return state.searchNotificationsTotalPage
  },
  popupOrderOptions(state) {
    return state.popupOrderOptions
  },
  appVersionOptions(state) {
    return state.appVersionOptions
  },
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

function getDate(date) {
  if (!date) return ''
  const dt = DateTime.fromISO(date)
  const dateOnly = dt.toISODate()
  return dateOnly
}

function cleanParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([key, value]) => value !== null)
  )
}

function getTimeHMFromTimestamptz(timestampString) {
  if (!timestampString) return ''
  return new Date(timestampString).toTimeString().split(' ')[0].substring(0, 5)
}

function snakeToPascalCase(str) {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
}

function getTimeFromISODate(d) {
  let date = new Date(d)
  const options = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
    timeZone: 'Asia/Bangkok',
  }
  const localTime = new Intl.DateTimeFormat(undefined, options).formatToParts(
    date
  )

  const hourPart = localTime.find((part) => part.type === 'hour')
  const minutePart = localTime.find((part) => part.type === 'minute')
  let time = `${hourPart.value == '24' ? '00' : hourPart.value}:${minutePart.value}`
  return time
}

export default {
  state,
  mutations,
  actions,
  getters,
}
